import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import CultivarForm from '../../components/cultivar/CultivarForm';
import { Leaf, CheckCircle, Loader2 } from 'lucide-react';
import ModuleAccessDenied from '../../components/ui/ModuleAccessDenied';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import type { CultivarFormData } from '../../types/cultivar';
import type { ProtectionRow } from '../../components/cultivar/CultivarForm';
import cultivarService from '../../services/cultivar';

function CultivarCreate() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successId, setSuccessId] = useState<number | null>(null);
  // The server's cultivarWrite gate: registering needs a pbr or admin seat.
  const { hasAccess, loading: licLoading } = useModuleLicense('cultivar', ['pbr', 'admin']);

  const handleSubmit = async (
    data: CultivarFormData,
    protections: ProtectionRow[],
    programIds: number[]
  ) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await cultivarService.createCultivar(data);
      if (response.success && response.data) {
        const newId = response.data.id;
        // Save protections if any
        if (protections.length > 0) {
          await cultivarService.bulkUpsertProtections(newId, protections);
        }
        // Assign programs if any
        for (const programId of programIds) {
          await cultivarService.addCultivarProgram(newId, programId);
        }
        setSuccessId(newId);
        // Brief delay to show success state before navigating
        setTimeout(() => {
          navigate(`/cultivar/${response.data!.id}`);
        }, 1500);
      } else {
        setError((response as any).message || 'Failed to create cultivar');
      }
    } catch (err) {
      console.error('Error creating cultivar:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/cultivar');
  };

  if (licLoading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary-600" /></div>;
  }
  if (!hasAccess) return <ModuleAccessDenied moduleName="Cultivar (pbr or admin seat)" />;

  // Success State
  if (successId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader
          title="Register New Cultivar"
          subtitle="Add a new cultivar to the registry"
          icon={<Leaf className="h-6 w-6 text-white" />}
        />

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="p-12 text-center">
              <div className="h-16 w-16 rounded-full bg-success-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-success-600" />
              </div>
              <h2 className="text-2xl font-bold text-neutral-900 mb-2">
                Cultivar Registered Successfully!
              </h2>
              <p className="text-neutral-600">
                Redirecting to cultivar details...
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Register New Cultivar"
        subtitle="Add a new cultivar to the registry with IP protection details"
        icon={<Leaf className="h-6 w-6 text-white" />}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Error Banner */}
        {error && (
          <div className="mb-6 bg-error-50 border border-error-200 rounded-lg p-4">
            <p className="text-error-700">{error}</p>
          </div>
        )}

        <Card>
          <CardContent className="p-6 sm:p-8">
            <CultivarForm
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              isSubmitting={isSubmitting}
            />
          </CardContent>
        </Card>

        {/* Info Panel */}
        <div className="mt-6 bg-primary-50 border border-primary-200 rounded-lg p-6">
          <h3 className="font-semibold text-primary-900 mb-3">
            Registration Requirements
          </h3>
          <ul className="space-y-2 text-sm text-primary-800">
            <li className="flex items-start gap-2">
              <span className="text-primary-500 mt-0.5">•</span>
              <span>
                <strong>IP Protection:</strong> At least one form of intellectual property
                protection (PBR or Patent) must be provided for scion cultivars.
                Rootstock cultivars can be registered without IP details.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary-500 mt-0.5">•</span>
              <span>
                <strong>Observations:</strong> Once created, record attribute observations
                (Brix, fruit size, disease resistance, etc.) on the cultivar's Observations tab.
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default CultivarCreate;
